/**
 * SOLID Review
 * S — Strong. One concern: hand each delivery to the handler of its kind.
 * O — Strong. A new kind of delivery is one handler in the map; the map's type refuses a kind left out.
 * L — Strong. Implements `IPortClientServerEvents` exactly.
 * I — Strong. One op.
 * D — Strong. The handlers it is handed, by their use-case role.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPayloadClientServerEvent, IPortClientServerEvents } from '../ports/ClientServerEvents.contract';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';

type TServerEventKind = IPayloadClientServerEvent['type'];
/** A kind of delivery's payload, by kind. */
export type TServerEventPayload<K extends TServerEventKind> = Extract<IPayloadClientServerEvent, { readonly type: K }>['payload'];
/** One handler per kind of delivery — every kind, or tsc refuses the map. */
export type TServerEventHandlers = { readonly [K in TServerEventKind]: IUseCase<TServerEventPayload<K>, TPortOutcome> };
/** A delivery of one of the kinds `K`, its payload typed by its kind. */
type TServerEventOf<K extends TServerEventKind> = { readonly [P in K]: { readonly type: P; readonly payload: TServerEventPayload<P> } }[K];

/**
 * THE SERVER'S EVENTS, DISPATCHED (frontend-application-oo P3, 2026-10-28) — the `ClientServerEvents` lollipop's
 * implementation, in the backend's `OutboxConsumers` shape: a map from the kind of a delivery to its handler. It replaced the
 * table's `deliver` switch and the provided port's `routed` chain (the consent platform's changes to the legal side, a pushed
 * balance to the payment side, the rest to the table). Unlike the outbox's kinds, a delivery's are a closed union the
 * contract has already parsed, so a kind no handler takes cannot arrive — and a map that leaves one out does not compile.
 */
export class ServerEvents implements IPortClientServerEvents {
  constructor(private readonly handlers: TServerEventHandlers) {}

  deliver(ctx: IContextRequest, event: IPayloadClientServerEvent): Promise<TPortOutcome> {
    return this.handle(ctx, event);
  }

  private handle<K extends TServerEventKind>(ctx: IContextRequest, event: TServerEventOf<K>): Promise<TPortOutcome> {
    return this.handlers[event.type].execute(ctx, event.payload);
  }
}
